import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { fetchUsers } from '../api/tas';
import { SupportGroup, SupportGroupFormData, SupportGroupLevel } from '../models/supportGroup';

type Option = { id: string; label: string };

export const useSupportGroupForm = (
  group: SupportGroup | null,
  onSave: (data: SupportGroupFormData) => Promise<string | null>,
) => {
  const intl = useIntl();
  const [name, setName] = useState(group?.name ?? '');
  const [level, setLevel] = useState<SupportGroupLevel>(group?.level ?? 'N1');
  const [parentGroupId, setParentGroupId] = useState<number | null>(group?.parentGroupId ?? null);
  const [userIds, setUserIds] = useState<string[]>(group?.users.map(user => user.id) ?? []);
  const [userOptions, setUserOptions] = useState<Option[]>([]);
  const [nameError, setNameError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers()
      .then((users: { id: string; firstName: string; lastName: string }[]) => {
        const options = users.map(user => ({ id: user.id, label: `${user.firstName} ${user.lastName}` }));
        const missingMembers = (group?.users ?? [])
          .filter(member => !options.some(option => option.id === member.id))
          .map(member => ({ id: member.id, label: member.name || member.email || member.id }));
        setUserOptions([...options, ...missingMembers]);
      })
      .catch(() => setUserOptions([]));
  }, [group]);

  // O campo "grupo pai" está oculto na UI: o parentGroupId existente é preservado no update,
  // mas zerado ao trocar de nível (o back exige o mesmo nível entre pai e subgrupo).
  const changeLevel = (value: SupportGroupLevel) => {
    setLevel(value);
    setParentGroupId(null);
  };

  const submit = async () => {
    if (!name.trim()) {
      setNameError(intl.formatMessage({ id: 'intl-msg:backoffice.supportGroupForm.nameRequired' }));
      return;
    }
    setNameError('');
    setSubmitError('');
    setSaving(true);
    const error = await onSave({ name: name.trim(), level, parentGroupId, userIds });
    setSaving(false);
    if (error) setSubmitError(error);
  };

  return {
    name,
    setName,
    level,
    changeLevel,
    userIds,
    setUserIds,
    userOptions,
    nameError,
    submitError,
    saving,
    submit,
  };
};
